// Boot capability checks (§23.1): WebGL2 probe and the no-WebGL fallback screen, font loading,
// the initial quality tier and the WebSocket endpoint.
import type { Params } from './core/params';
import type { Settings } from './profile/settings';
import { initialTier, probeDevice } from './render/quality';
import type { QualityTier } from './render/Renderer';
import { BASE_URL } from './core/base';

/** Font faces the UI uses (§14.1); loaded explicitly because @font-face loads lazily. */
const FONT_FACES = ['400 1em "Lilita One"', '400 1em Nunito', '700 1em Nunito', '800 1em Nunito'] as const;
const FONT_TIMEOUT_MS = 4000;

/** True if the browser can create a WebGL2 context. The probe context is released immediately. */
export function hasWebGL2(): boolean {
  try {
    const gl = document.createElement('canvas').getContext('webgl2');
    if (!gl) return false;
    gl.getExtension('WEBGL_lose_context')?.loseContext();
    return true;
  } catch {
    return false;
  }
}

/** Shows the full-screen "needs WebGL2" message from index.html and hides the game canvas. */
export function showFallback(): void {
  document.getElementById('game')?.setAttribute('hidden', '');
  document.getElementById('fallback')?.removeAttribute('hidden');
}

/**
 * Loads every UI font face, then waits for `document.fonts.ready`. Resolves false (system fonts
 * stay in use) if loading fails or takes longer than 4 s; boot never blocks on fonts.
 */
export async function loadFonts(): Promise<boolean> {
  if (!('fonts' in document)) return false;
  const loading = Promise.all(FONT_FACES.map((f) => document.fonts.load(f)))
    .then(() => document.fonts.ready)
    .then(() => FONT_FACES.every((f) => document.fonts.check(f)))
    .catch(() => false);
  const timeout = new Promise<boolean>((resolve) => setTimeout(() => resolve(false), FONT_TIMEOUT_MS));
  return Promise.race([loading, timeout]);
}

/** Initial quality tier (§24.3): `?quality=`, else the setting, else device heuristics. */
export function detectQuality(params: Params, settings: Settings): QualityTier {
  return initialTier(params.quality, settings.graphics.quality, probeDevice());
}

/** `wss?://<host><base>ws` for this page (§20.1); Vite proxies it in development. */
export function wsUrl(): string {
  return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${BASE_URL}ws`;
}
